import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Inbox } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { EVENT_LABELS } from "@/lib/constants";
import { formatDate } from "@/lib/format";

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return formatDate(iso);
}

export function NotificationBell({ refreshKey }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ events: [], unread_count: 0 });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get("/notifications/center")).data); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const markAllRead = async () => {
    await api.post("/notifications/center/read");
    load();
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative w-9 h-9 rounded-full grid place-items-center hover:bg-oatmeal transition-colors" data-testid="notification-bell" aria-label="Notifications">
          <Bell size={19} className="text-midnight" />
          {data.unread_count > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-status-danger text-white text-[10px] font-bold grid place-items-center border-2 border-white" data-testid="notification-unread-count">
              {data.unread_count > 9 ? "9+" : data.unread_count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-center">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <p className="font-heading font-bold text-sm">Notifications</p>
          {data.events.some((e) => !e.read) && (
            <button onClick={markAllRead} className="text-xs text-brandblue font-semibold flex items-center gap-1 hover:underline" data-testid="mark-all-read">
              <CheckCheck size={13} /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading && data.events.length === 0 ? (
            <p className="p-6 text-sm text-ink-muted text-center">Loading…</p>
          ) : data.events.length === 0 ? (
            <div className="p-8 text-center" data-testid="notification-empty">
              <span className="inline-grid place-items-center w-12 h-12 rounded-2xl bg-brandblue-light mx-auto mb-3"><Inbox size={22} className="text-brandblue" /></span>
              <p className="font-semibold text-sm text-midnight">You're all caught up.</p>
              <p className="text-xs text-ink-muted mt-1">No new money updates right now.</p>
            </div>
          ) : (
            data.events.map((e) => (
              <div key={e.id} className={`px-4 py-3 border-b last:border-0 flex gap-3 ${e.read ? "" : "bg-brandblue-light/40"}`} data-testid={`notif-${e.event_type}`}>
                {!e.read && <span className="w-1.5 h-1.5 rounded-full bg-brandblue mt-1.5 shrink-0" />}
                <div className={e.read ? "pl-3.5" : ""}>
                  <p className="text-sm text-midnight font-medium">{EVENT_LABELS[e.event_type] || e.event_type}</p>
                  <p className="text-xs text-ink-muted mt-0.5">{timeAgo(e.created_at)}</p>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="border-t p-2">
          <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => { setOpen(false); navigate("/dashboard/notifications"); }} data-testid="view-all-activity">
            View all activity
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
